import React, { Fragment } from 'react';
import { Button } from '../button';
import classNames from 'classnames';

interface TabProps {
  children?: JSX.Element | JSX.Element[];
  size?: 'sm' | 'md';
  mode?: 'vr' | 'hr';
  compact?: boolean;
  className?: string;
  active?: number;
  setActiveTab?: (param: number) => void;
}

interface HeadProps {
  children?: JSX.Element | JSX.Element[];
  size?: 'sm' | 'md';
  mode?: 'vr' | 'hr';
  compact?: boolean;
  className?: string;
  active?: number;
  setActiveTab?: (param: number) => void;
}

interface TabItemProps {
  children?: JSX.Element | JSX.Element[];
  label?: string;
  index?: number;
  compact?: boolean;
  mode?: 'vr' | 'hr';
  size?: 'sm' | 'md';
  active?: number;
  disabled?: boolean;
  setActiveTab?: (param: number) => void;
}

interface ContentProps {
  children?: JSX.Element | JSX.Element[];
  label?: string;
  index?: number;
  active?: number;
}

export const Tab = ({
  children,
  compact = false,
  mode = 'hr',
  size = 'md',
  className,
  active,
  setActiveTab,
}: TabProps) => {
  return (
    <div
      className={classNames('d-flex', 'gap-4', {
        'flex-column': mode === 'hr',
      })}
    >
      {Array.isArray(children)
        ? children.map((child, key) =>
            React.cloneElement(child, {
              key,
              compact,
              mode,
              className,
              size,
              active,
              setActiveTab,
            }),
          )
        : React.cloneElement(children, {
            compact,
            mode,
            className,
            size,
            active,
            setActiveTab,
          })}
    </div>
  );
};

//Tab Header Component
const Heads = ({
  children,
  compact,
  mode,
  className,
  size,
  active,
  setActiveTab,
}: HeadProps) => (
  <div
    className={classNames(
      'p-2',
      'bg-light',
      'd-flex',
      'gap-4',
      'border',
      'rounded-4',
      'overflow-auto',
      {
        'flex-column': mode === 'vr',
      },
      className,
    )}
  >
    {Array.isArray(children) ? (
      children?.map((child, idx) => (
        <Fragment key={idx}>
          {React.cloneElement(child, {
            compact,
            mode,
            size,
            active,
            setActiveTab,
          })}
        </Fragment>
      ))
    ) : (
      <Fragment>
        {React.cloneElement(children, {
          compact,
          mode,
          size,
          active,
          setActiveTab,
        })}
      </Fragment>
    )}
  </div>
);

const Item = ({
  label,
  index,
  compact,
  mode,
  size,
  active,
  disabled = false,
  setActiveTab,
}: TabItemProps) => {
  const handleClickOnTabItem = () => setActiveTab(index);

  return (
    label && (
      <Button
        fill={active === index ? 'fill' : 'transparent'}
        size={size}
        onClick={() => {
          handleClickOnTabItem();
        }}
        className={classNames('text-subtitle2', {
          'flex-fill': compact,
          'justify-content-start': mode === 'vr',
          'shadow-sm': active === index,
        })}
        disabled={disabled}
      >
        {label}
      </Button>
    )
  );
};

//Tab content wrapper component
const ContentWrapper = ({ children, active }: ContentProps) => (
  <div>
    {Array.isArray(children) ? (
      children.map((child, index) => (
        <Fragment key={index}>{React.cloneElement(child, { active })}</Fragment>
      ))
    ) : (
      <Fragment>{React.cloneElement(children, { active })}</Fragment>
    )}
  </div>
);

//Tab each content component
const Content = ({ children, index, active }: ContentProps) => {
  return index === active ? <div> {children} </div> : <></>;
};

Tab.Heads = Heads;
Tab.Item = Item;
Tab.ContentWrapper = ContentWrapper;
Tab.Content = Content;
